//@flow
import React, { useMemo, useState } from "react";
import { Keyboard } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import {
  changeSignupInput,
  signupRequest,
} from "@microdoing/shared/services/signup/actions";
import {
  changeEmail,
  changePassword,
  loginRequest,
} from "@microdoing/shared/services/login/actions";
import i18n from "@microdoing/shared/i18n";
import {
  getSignupAccessCode,
  getSignupClientName,
  getSignupEmail,
  getSignupErrors,
  getSignupPassword,
  getSignupPolicy,
  getSignupRequesting,
} from "@microdoing/shared/services/signup/selectors";
import { useNavigation } from "@react-navigation/native";
import {
  RoundInput,
  Box,
  MainTitle,
  MainText,
  MainButton,
  PressableOpacity,
} from "app/components/common";
import sanitizeName from "app/utils/sanitizeName";
import ClientPolicyCheck from "../ClientPolicyCheck";
import SignupPolicy from "../SignupPolicy";
import sanitizeAccessCode from "app/utils/sanitizeAccessCode";
import HeaderScreen from "app/components/common/HeaderScreen";
import KeyboardAvoidWrapper from "app/components/common/KeyboardAvoidWrapper";
import normalize from "app/utils/normalize";
import theme from "app/Theme/Theme";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import WebSetupEmail from "./WebSetupEmail";
import { useSafeAreaInsets } from "react-native-safe-area-context";

const SignupCreateAccount = () => {
  const [isClientPolicyChecked, setIsClientPolicyChecked] = useState(false);

  const requesting = useSelector(getSignupRequesting);
  const email = useSelector(getSignupEmail);
  const password = useSelector(getSignupPassword);
  const accessCode = useSelector(getSignupAccessCode);
  const errors = useSelector(getSignupErrors);
  const clientName = useSelector(getSignupClientName);
  const signupPolicy = useSelector(getSignupPolicy);
  const insets = useSafeAreaInsets();

  const isButtonDisable = useMemo(() => {
    return (
      email === "" ||
      password === "" ||
      requesting ||
      (!!signupPolicy && !isClientPolicyChecked)
    );
  }, [email, password, requesting, signupPolicy, isClientPolicyChecked]);

  const dispatch = useDispatch();
  const navigation = useNavigation();

  const onCheckPolicy = () => {
    setIsClientPolicyChecked(!isClientPolicyChecked);
  };

  const onChangeEmail = email => {
    dispatch(changeSignupInput({ email }));
  };

  const onChangePassword = password => {
    dispatch(changeSignupInput({ password }));
  };

  const goNext = () => {
    // Apple testing
    if (email === "clement@pulse-experience.co") {
      dispatch(
        loginRequest({
          email: sanitizeName(email),
          password: password,
        }),
      );
      Keyboard.dismiss();
    } else {
      dispatch(
        signupRequest({
          email: sanitizeName(email),
          password: password,
          accessCode: sanitizeAccessCode(accessCode),
        }),
      );
    }
  };

  const goToLogin = () => {
    dispatch(changeEmail(email));
    dispatch(changePassword(password));
    navigation.navigate("Login");
  };

  const renderBody = () => {
    return (
      <>
        <RoundInput
          containerPropStyle={styles.input}
          testID="signup-email-input"
          textContentType="emailAddress"
          name="email"
          autoCapitalize="none"
          onChangeText={onChangeEmail}
          placeholder={i18n.t("signup-email")}
          value={email}
          keyboardType="email-address"
          returnKeyType="done"
          autoFocus
        />
        <RoundInput
          containerPropStyle={styles.input}
          testID="signup-password-input"
          name="password"
          textContentType="oneTimeCode"
          keyboardType="default"
          autoCapitalize="none"
          onChangeText={onChangePassword}
          placeholder={i18n.t("signup-password")}
          onSubmitEditing={goNext}
          value={password}
          secureTextEntry={true}
          returnKeyType="done"
        />
        <Box>
          {/* 
            // ! warning ici la checkbox ne va pas, il nous faut un nouveaux composent 
            */}
          {!!signupPolicy && (
            <ClientPolicyCheck
              onCheckPolicy={onCheckPolicy}
              isClientPolicyChecked={isClientPolicyChecked}
              signupPolicy={signupPolicy}
            />
          )}
          <SignupPolicy />
          {errors.password !== "" ? (
            <MainText
              variant="l3-medium"
              marginTop="xxs"
              textAlign="center"
              color="system.red.950"
            >
              {errors.password || "bjour"}
            </MainText>
          ) : null}
          {
            // ? pourquoi ? et pas &&
          }
          {errors.email !== "" ? (
            <MainText
              variant="l3-medium"
              color="system.red.950"
              marginTop="xxs"
              textAlign="center"
            >
              {errors.email}
            </MainText>
          ) : null}
          {errors.name === "ClientNotFound" && (
            <MainText
              variant="l4-medium"
              marginTop="xxs"
              color="system.red.950"
              textAlign="center"
            >
              {i18n.t("signup-companyNotFound")}
            </MainText>
          )}
          {errors.name === "Validation Error" && (
            <PressableOpacity onPress={goToLogin} testID="login-text">
              <MainText
                variant="l4-semibold"
                textAlign="center"
                textDecorationLine="underline"
                marginTop="xs"
              >
                {i18n.t("signup-existing-account")}
                <MainText variant="l4-semibold">
                  {i18n.t("signup-existing-account-link")}
                </MainText>
              </MainText>
            </PressableOpacity>
          )}
        </Box>
      </>
    );
  };

  if (isWebDesktop) {
    return (
      <WebSetupEmail
        goNext={goNext}
        isButtonDisable={isButtonDisable}
        body={renderBody()}
      />
    );
  }

  return (
    <Box
      variant="screenView"
      backgroundColor="neutral.white"
      style={{ marginTop: insets.top }}
    >
      <HeaderScreen title="" leftIcon />
      <KeyboardAvoidWrapper>
        <>
          <MainTitle variant="l8-bold">{clientName}</MainTitle>
          <MainText variant="l5-medium" marginTop="xs">
            {i18n.t("create-account")}
          </MainText>
          <Box marginTop="sm" marginBottom="m">
            {renderBody()}
          </Box>
          <MainButton
            testID="signup-create-account-submit"
            isLoading={requesting}
            text={i18n.t("create-my-account")}
            onPress={goNext}
            isDisabled={isButtonDisable}
          />
        </>
      </KeyboardAvoidWrapper>
    </Box>
  );
};

const styles = {
  input: {
    height: normalize(57),
    borderRadius: 4,
    marginBottom: theme.spacing.m,
  },
};

export default SignupCreateAccount;
